import { create } from 'zustand'

export const TIP_CALCULATOR_CACHE_KEY = 'bookkeeper:tip-calculator-cache'
export const TIP_CALCULATOR_CACHE_TTL_MS = 60 * 60 * 1000

type CachedPayload = {
  savedAt: number
  startDate: string
  endDate: string
  activeRunId: number | null
}

interface TipCalculatorState {
  startDate: string
  endDate: string
  activeRunId: number | null
  cachedAt: number | null
  setStartDate: (startDate: string) => void
  setEndDate: (endDate: string) => void
  setActiveRunId: (runId: number | null) => void
  expireIfStale: () => boolean
}

function loadFromStorage(): CachedPayload | null {
  try {
    const raw = localStorage.getItem(TIP_CALCULATOR_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedPayload
    if (!parsed?.savedAt) return null
    if (Date.now() - parsed.savedAt >= TIP_CALCULATOR_CACHE_TTL_MS) {
      localStorage.removeItem(TIP_CALCULATOR_CACHE_KEY)
      return null
    }
    return parsed
  } catch {
    localStorage.removeItem(TIP_CALCULATOR_CACHE_KEY)
    return null
  }
}

function saveToStorage(state: TipCalculatorState): void {
  if (state.cachedAt == null) return
  const payload: CachedPayload = {
    savedAt: state.cachedAt,
    startDate: state.startDate,
    endDate: state.endDate,
    activeRunId: state.activeRunId,
  }
  localStorage.setItem(TIP_CALCULATOR_CACHE_KEY, JSON.stringify(payload))
}

function touchCache(state: TipCalculatorState): TipCalculatorState {
  const cachedAt = Date.now()
  const next = { ...state, cachedAt }
  saveToStorage(next)
  return next
}

const initialCache = loadFromStorage()

export const useTipCalculatorStore = create<TipCalculatorState>((set, get) => ({
  startDate: initialCache?.startDate ?? '',
  endDate: initialCache?.endDate ?? '',
  activeRunId: initialCache?.activeRunId ?? null,
  cachedAt: initialCache?.savedAt ?? null,

  setStartDate: (startDate) => set((state) => touchCache({ ...state, startDate })),
  setEndDate: (endDate) => set((state) => touchCache({ ...state, endDate })),
  setActiveRunId: (activeRunId) => set((state) => touchCache({ ...state, activeRunId })),

  expireIfStale: () => {
    const { cachedAt } = get()
    if (cachedAt == null) return false
    if (Date.now() - cachedAt < TIP_CALCULATOR_CACHE_TTL_MS) return false
    localStorage.removeItem(TIP_CALCULATOR_CACHE_KEY)
    set({ startDate: '', endDate: '', activeRunId: null, cachedAt: null })
    return true
  },
}))
